/**
 * ───────────────────────────────────────────────────────────────────────────
 * Import duties & taxes estimate — PDP "Shipping and taxes" accordion
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Fetches an indicative customs duties / import taxes estimate from
 * pro.oblist.com and injects it into the accordion, replacing the generic
 * "you may be subject to customs duties" copy ONLY when the API can actually
 * estimate (`estimable: true`). A "Calculate your duties" button opens a
 * modal with the full breakdown (duties / taxes / fees / total) and a small
 * form so the buyer can simulate other destinations (country / postal).
 *
 * Spec: docs/import-duties-estimate-theme-integration.md (oblist-starter).
 *
 * Two strictly separate estimate pipelines:
 *
 *   ACCORDION LINE — always the buyer's Shopify Markets country (+ region /
 *   postal from their saved address when it matches). Re-fetched on
 *   variant:change and when the Trade overlay rewrites the price. NEVER
 *   affected by what the buyer types in the modal.
 *
 *   MODAL SIMULATOR — starts from the Markets destination, lets the buyer
 *   change country / postal freely. Results render in the modal only. The
 *   Postal (same Markets country) is kept in sessionStorage so other PDPs
 *   reuse it for the accordion estimate + modal pre-fill. Discarded when
 *   the Markets country changes.
 *
 * Price (`value`) = what the buyer actually sees: the B2B Trade overlay
 * price when `sale-price` was rewritten ("Trade Price: …"), otherwise the
 * retail Markets variant price. For B2B customers the first fetch waits
 * (bounded poll) for the Trade overlay so the estimate is computed on the
 * trade price directly; a MutationObserver keeps it in sync afterwards.
 *
 * Failure contract:
 *   - `applicable: false` → same-zone green copy
 *   - `below_threshold` / `estimate.total === 0` → green copy explaining the
 *     local customs de-minimis threshold (e.g. A$1,000 for Australia)
 *   - `estimable: false` / network error → keep generic Liquid copy
 * ───────────────────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  if (window.__oblistImportDuties) return;
  window.__oblistImportDuties = true;

  var API_URL = 'https://pro.oblist.com/api/import-duties-estimate';
  var HELP_URL = 'https://help.oblist.com/en/articles/9527718-do-i-have-to-pay-customs-fees-for-my-order';
  var STORAGE_KEY = 'oblist:import-duties-dest';
  // Ignore price wobbles below this — only material moves trigger a re-fetch.
  var MATERIAL_CHANGE_PCT = 1;
  // Same country-default postals as pro.oblist.com shipping quote-engine —
  // used when the buyer hasn't set one yet, so the estimate always has a real
  // postal and we can show it (never a vague "add postal code").
  var DEFAULT_POSTALS = {
    US: '10001', CA: 'M5H', GB: 'SW1A 1AA', FR: '75001', DE: '10115',
    ES: '28001', IT: '00184', NL: '1011', BE: '1000', CH: '8001',
    AT: '1010', PT: '1100-001', IE: 'D02', DK: '1050', SE: '11122',
    NO: '0150', FI: '00100', PL: '00-001', CZ: '11000', AU: '2000',
    JP: '100-0001', HK: '999077', SG: '018956', AE: '00000', NZ: '1010',
    KR: '04524', IL: '6100000', BR: '01310-100', MX: '06600', IN: '110001'
  };

  function defaultPostal(country) {
    return DEFAULT_POSTALS[String(country || '').toUpperCase()] || '';
  }

  // How long the first B2B fetch waits for the Trade overlay to land.
  var TRADE_WAIT_MS = 4000;

  var I18N = window.IMPORT_DUTIES_I18N || {};
  var COUNTRIES = window.IMPORT_DUTIES_COUNTRIES || [];

  var root = document.querySelector('[data-import-duties]');
  if (!root) return;

  var resultEl = root.querySelector('[data-import-duties-result]');
  var fallbackEl = root.querySelector('[data-import-duties-fallback]');
  var originEl = root.querySelector('[data-import-duties-origin]');
  if (!resultEl || !fallbackEl) return;

  var marketCountry = (root.dataset.country || '').toUpperCase();
  var originName = (root.dataset.origin || '').trim();
  function countryName(iso) {
    var code = String(iso || '').toUpperCase();
    if (!code) return '';
    for (var i = 0; i < COUNTRIES.length; i++) {
      if (COUNTRIES[i].iso === code) return COUNTRIES[i].name;
    }
    return code;
  }
  var destinationName = countryName(marketCountry);

  // Snapshot of the SSR "Ships from …" markup so we can restore it when the
  // estimate is not applicable (same zone / fallback).
  var originMarkup = originEl ? originEl.innerHTML : '';

  var state = {
    productId: root.dataset.productId || '',
    currency: root.dataset.currency || 'EUR',
    locale: (root.dataset.locale || 'en').toLowerCase().indexOf('fr') === 0 ? 'fr' : 'en',
    isB2B: root.dataset.b2b === 'true',
    // Retail Markets price in major units — kept in sync via variant:change.
    basePrice: parseFloat(root.dataset.price) || 0,

    // Accordion pipeline (Markets destination — immutable).
    marketDest: {
      country: marketCountry,
      region: root.dataset.region || '',
      postal: root.dataset.postal || ''
    },
    lastData: null,
    lastKey: '',
    fetchedPrice: 0,
    abort: null,
    debounceTimer: null,

    // Modal simulator pipeline.
    simDest: null,
    modalData: null,
    modalKey: '',
    modalAbort: null
  };

  // Postal resolution (same Markets country only):
  //   1) buyer-confirmed postal from a previous modal update (session)
  //   2) customer default address zip from Liquid
  //   3) country default (e.g. US → 10001) — so we always estimate AND display
  //      a concrete postal the buyer can change.
  var savedDest = loadDest();
  if (savedDest && savedDest.country === marketCountry && savedDest.postal) {
    state.marketDest.postal = savedDest.postal;
  } else if (!state.marketDest.postal) {
    state.marketDest.postal = defaultPostal(marketCountry);
  }
  state.simDest = {
    country: state.marketDest.country,
    postal: state.marketDest.postal || ''
  };

  if (!state.productId || !state.marketDest.country || !(state.basePrice > 0)) return;

  // ── i18n ─────────────────────────────────────────────────────────────────

  // Shopify's `t` filter HTML-escapes translation values (only `_html` keys
  // are exempt), so "duties & taxes" reaches us as "duties &amp; taxes".
  // Decode once here — everything is re-escaped properly at render time.
  var entityDecoder = document.createElement('textarea');

  function decodeEntities(value) {
    if (value.indexOf('&') === -1) return value;
    entityDecoder.innerHTML = value;
    return entityDecoder.value;
  }

  function t(key, fallback) {
    var value = I18N[key] || fallback || '';
    return value ? decodeEntities(String(value)) : '';
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  // ── Simulator destination persistence (form pre-fill only) ──────────────

  function loadDest() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.country) return null;
      // Saved under a different Markets country → stale, discard.
      if (parsed.market && parsed.market !== marketCountry) return null;
      return {
        country: String(parsed.country).toUpperCase(),
        postal: parsed.postal || ''
      };
    } catch (e) {
      return null;
    }
  }

  function saveDest(dest) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        country: dest.country,
        postal: dest.postal,
        market: marketCountry
      }));
    } catch (e) {
      /* private browsing — the simulator just won't pre-fill next time */
    }
  }

  // ── Buyer price ──────────────────────────────────────────────────────────
  // Base = retail Markets price (Liquid data attr + variant:change detail).
  // When the B2B Trade overlay rewrote the main sale-price element
  // ("Trade Price: …" / "Prix Trade : …"), that displayed amount wins —
  // duties are estimated on the transaction value the buyer actually sees.

  function mainPriceEl() {
    var scope = document.querySelector(
      '.shopify-section--main-product, .main-product, [data-main-product], .product-info'
    );
    return scope ? scope.querySelector('sale-price') : document.querySelector('sale-price');
  }

  function readTradePrice() {
    var el = mainPriceEl();
    if (!el) return 0;
    var text = el.textContent || '';
    var lower = text.toLowerCase();
    if (lower.indexOf('trade price') === -1 && lower.indexOf('prix trade') === -1) return 0;
    return parsePriceText(text.replace(/(?:trade\s*price|prix\s*trade)\s*:?\s*/i, ''));
  }

  // Handles "1,234.56" (US) as well as "1.234,56" / "3.600" (EU) formats —
  // same heuristics as ProProjectsUI.extractTradePrice in pro-projects.js.
  function parsePriceText(text) {
    var numeric = String(text).replace(/[^\d.,]/g, '');
    if (!numeric) return 0;
    var lastComma = numeric.lastIndexOf(',');
    var lastDot = numeric.lastIndexOf('.');
    var price = 0;
    if (lastComma > lastDot) {
      // European decimal comma: 1.234,56 → 1234.56
      price = parseFloat(numeric.replace(/\./g, '').replace(',', '.'));
    } else if (lastDot !== -1 && lastComma === -1 && numeric.length - lastDot - 1 === 3) {
      // European thousands dot with no decimals: 3.600 → 3600
      price = parseFloat(numeric.replace(/\./g, ''));
    } else if (lastDot !== -1) {
      // US format: 1,234.56 → 1234.56
      price = parseFloat(numeric.replace(/,/g, ''));
    } else if (lastComma !== -1) {
      price = parseFloat(numeric.replace(',', '.'));
    } else {
      price = parseFloat(numeric);
    }
    return isFinite(price) && price > 0 ? price : 0;
  }

  function effectivePrice() {
    var traded = readTradePrice();
    return traded > 0 ? traded : state.basePrice;
  }

  // Bounded poll for the B2B Trade overlay (calTradingPrice rewrites
  // sale-price asynchronously — on Direct Buy it even awaits a pricing API).
  function waitForTradePrice(maxMs) {
    return new Promise(function (resolve) {
      var startedAt = Date.now();
      (function poll() {
        var traded = readTradePrice();
        if (traded > 0) return resolve(traded);
        if (Date.now() - startedAt >= maxMs) return resolve(0);
        setTimeout(poll, 300);
      })();
    });
  }

  function formatAmount(value, currency) {
    var num = typeof value === 'number' ? value : parseFloat(value) || 0;
    try {
      return new Intl.NumberFormat(state.locale === 'fr' ? 'fr-FR' : 'en-US', {
        style: 'currency',
        currency: currency || state.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(num);
    } catch (e) {
      return Math.round(num) + ' ' + (currency || state.currency);
    }
  }

  // ── Fetch plumbing ───────────────────────────────────────────────────────

  function destKey(price, dest) {
    return [
      state.productId, dest.country, dest.region || '', dest.postal || '',
      price.toFixed(2), state.currency
    ].join('|');
  }

  function requestEstimate(dest, price, ctrl, scope) {
    var url = API_URL +
      '?product_id=' + encodeURIComponent(state.productId) +
      '&destination_country=' + encodeURIComponent(dest.country) +
      '&value=' + encodeURIComponent(price.toFixed(2)) +
      '&currency=' + encodeURIComponent(state.currency);
    if (dest.region) url += '&destination_region=' + encodeURIComponent(dest.region);
    if (dest.postal) url += '&destination_postal=' + encodeURIComponent(dest.postal);

    // Audit trail: confirms which price the estimate was computed on.
    if (window.console && console.debug) {
      console.debug(
        '[import-duties] ' + scope + ' → ' + dest.country + (dest.postal ? ' ' + dest.postal : '') +
        ' value=' + price.toFixed(2) + ' ' + state.currency +
        (readTradePrice() > 0 ? ' (trade price)' : ' (retail price)')
      );
    }

    return fetch(url, ctrl ? { signal: ctrl.signal } : undefined)
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (!data || typeof data !== 'object') throw new Error('bad payload');
        return data;
      });
  }

  // ── Accordion pipeline (Markets destination only) ────────────────────────

  function scheduleAccordionFetch(delay) {
    clearTimeout(state.debounceTimer);
    state.debounceTimer = setTimeout(runAccordionFetch, typeof delay === 'number' ? delay : 400);
  }

  function runAccordionFetch() {
    var price = effectivePrice();
    if (!(price > 0)) return;
    var key = destKey(price, state.marketDest);
    if (key === state.lastKey && state.lastData) return;

    if (state.abort) state.abort.abort();
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    state.abort = ctrl;

    requestEstimate(state.marketDest, price, ctrl, 'accordion')
      .then(function (data) {
        state.lastKey = key;
        state.fetchedPrice = price;
        state.lastData = data;
        renderAccordion(data);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        // Network / API failure — the generic Liquid copy stays untouched.
      });
  }

  // Duty de-minimis by destination (shipment value in that customs currency).
  // Display-only — the API decides when total is 0 / below_threshold.
  // Intentionally omitted (2026):
  //   US  — Section 321 suspended (no duty-free de minimis)
  //   EU  — €150 relief removed 1 Jul 2026 (flat €3 on low-value instead)
  //   CH  — threshold is on duty amount owed, not order value
  //   IN  — no de minimis (duties from the first unit of value)
  var DUTY_FREE_THRESHOLDS = {
    AU: { amount: 1000, currency: 'AUD' },
    NZ: { amount: 1000, currency: 'NZD' },
    GB: { amount: 135, currency: 'GBP' },
    CA: { amount: 20, currency: 'CAD' },
    JP: { amount: 10000, currency: 'JPY' },
    SG: { amount: 400, currency: 'SGD' },
    KR: { amount: 150, currency: 'USD' },
    AE: { amount: 1000, currency: 'AED' },
    NO: { amount: 3000, currency: 'NOK' },
    MX: { amount: 50, currency: 'USD' },
    BR: { amount: 50, currency: 'USD' },
    TR: { amount: 150, currency: 'EUR' },
    TH: { amount: 1500, currency: 'THB' }
  };

  var THRESHOLD_LOCALES = {
    AUD: 'en-AU', NZD: 'en-NZ', GBP: 'en-GB', CAD: 'en-CA',
    JPY: 'ja-JP', SGD: 'en-SG', USD: 'en-US', AED: 'en-AE',
    NOK: 'nb-NO', EUR: 'en-IE', THB: 'th-TH'
  };

  function isZeroDutyEstimate(data) {
    if (!data) return false;
    if (data.fallback === 'below_threshold') return true;
    if (!data.estimable || !data.estimate) return false;
    var total = Number(data.estimate.total);
    return Number.isFinite(total) && total <= 0;
  }

  function formatDutyThreshold(country) {
    var rule = DUTY_FREE_THRESHOLDS[String(country || '').toUpperCase()];
    if (!rule) return '';
    var localeTag = state.locale === 'fr'
      ? 'fr-FR'
      : (THRESHOLD_LOCALES[rule.currency] || 'en-US');
    try {
      return new Intl.NumberFormat(localeTag, {
        style: 'currency',
        currency: rule.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(rule.amount);
    } catch (e) {
      return rule.amount + ' ' + rule.currency;
    }
  }

  function belowThresholdAccordionText(destCountry) {
    var threshold = formatDutyThreshold(destCountry);
    if (!threshold) return null;
    if (originName) {
      return t(
        'below_threshold_line',
        'This piece ships from [origin]. No import duties are expected at the border — this order value is under the [threshold] customs threshold.'
      ).replace(/\[origin\]/g, originName).replace(/\[threshold\]/g, threshold);
    }
    return t(
      'below_threshold_line_no_origin',
      'No import duties are expected at the border — this order value is under the [threshold] customs threshold.'
    ).replace(/\[threshold\]/g, threshold);
  }

  function belowThresholdModalText(destCountry, data) {
    var threshold = formatDutyThreshold(destCountry);
    if (threshold) {
      return t(
        'below_threshold',
        'No import duties are expected at the border — this order value is under the [threshold] customs threshold.'
      ).replace(/\[threshold\]/g, threshold);
    }
    var api = data && data.message && (data.message[state.locale] || data.message.en);
    return api || t('same_zone', 'No import duties are expected for this destination.');
  }

  function renderGreenAccordion(text) {
    if (originEl) originEl.hidden = true;
    resultEl.innerHTML =
      '<span class="import-duties__same-zone">' + escapeHtml(text) + '</span>';
    resultEl.hidden = false;
    fallbackEl.hidden = true;
  }

  function renderAccordion(data) {
    if (data.applicable === false) {
      var sameZoneText = originName
        ? t('same_zone_line', 'This piece ships from [origin] — no import duties or taxes for your location.').replace(/\[origin\]/g, originName)
        : t('same_zone_line_no_origin', 'No import duties or taxes for your location.');
      renderGreenAccordion(sameZoneText);
      return;
    }
    if (isZeroDutyEstimate(data)) {
      var thresholdText = belowThresholdAccordionText(state.marketDest.country || marketCountry);
      if (!thresholdText) {
        var apiLine = data.message && (data.message[state.locale] || data.message.en);
        thresholdText = apiLine
          ? decodeEntities(String(apiLine))
          : t('same_zone_line_no_origin', 'No import duties or taxes for your location.');
      }
      renderGreenAccordion(thresholdText);
      return;
    }
    if (data.estimable && data.formatted && data.formatted.total) {
      var postal = (state.marketDest.postal || defaultPostal(marketCountry) || '').trim();
      if (postal && !state.marketDest.postal) state.marketDest.postal = postal;
      var amountBtn =
        '<button type="button" class="import-duties__amount" data-import-duties-open aria-label="' +
        escapeHtml(t('calculate_cta', 'Calculate your duties')) + '">' +
        escapeHtml(String(data.formatted.total)) +
        '</button>';
      // Underlined postal = the one used for this estimate; click to change it.
      var postalBtn = postal
        ? (
          ' <button type="button" class="import-duties__postal" data-import-duties-open title="' +
          escapeHtml(t('postal_label', 'Postal code')) + '">' +
          escapeHtml(postal) +
          '</button>'
        )
        : '';
      var noteHtml =
        '<span class="import-duties__note">' +
        escapeHtml(t('note', 'Not included in the product price — indicative only. Paid directly to the carrier at import, not to The Oblist.')) +
        '</span>';

      var template = originName
        ? t(
          'merged_line',
          'This piece ships from [origin] and your location is [destination][postal]. We estimate import duties & taxes at [amount] for your destination.'
        )
        : t(
          'estimate_line',
          'Your location is [destination][postal]. We estimate import duties & taxes at [amount] for your destination.'
        );

      var filled = template
        .replace(/\[origin\]/g, originName || '')
        .replace(/\[destination\]/g, destinationName || marketCountry || '');
      var postalParts = filled.split('[postal]');
      var afterPostal = (postalParts[1] || '');
      var amountParts = afterPostal.split('[amount]');

      if (originEl) originEl.hidden = true;
      resultEl.innerHTML =
        escapeHtml(postalParts[0] || '') +
        postalBtn +
        escapeHtml(amountParts[0] || '') +
        amountBtn +
        escapeHtml(amountParts[1] || '') +
        noteHtml;
      resultEl.hidden = false;
      fallbackEl.hidden = true;
      return;
    }

    // estimable: false (missing HS, Arta down…) — keep origin + generic copy.
    if (originEl && originMarkup) {
      originEl.innerHTML = originMarkup;
      originEl.hidden = false;
    }
    resultEl.hidden = true;
    fallbackEl.hidden = false;
  }

  // ── Modal simulator pipeline ─────────────────────────────────────────────

  var modal = null;
  var backdrop = null;
  var lastTrigger = null;
  var lockedScrollY = 0;

  function runModalFetch() {
    var price = effectivePrice();
    if (!(price > 0) || !state.simDest.country) return;
    var key = destKey(price, state.simDest);
    if (key === state.modalKey && state.modalData) {
      renderModal(state.modalData);
      return;
    }

    if (state.modalAbort) state.modalAbort.abort();
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    state.modalAbort = ctrl;

    setLoading(true);
    requestEstimate(state.simDest, price, ctrl, 'simulator')
      .then(function (data) {
        state.modalKey = key;
        state.modalData = data;
        setLoading(false);
        renderModal(data);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        setLoading(false);
        modal.querySelector('[data-duties-breakdown]').hidden = true;
        setStatus(t('error', "We couldn't calculate an estimate right now. Please try again."), 'error');
      });
  }

  function ensureModal() {
    if (modal) return;

    backdrop = document.createElement('div');
    backdrop.id = 'import-duties-backdrop';
    backdrop.className = 'import-duties__backdrop';

    modal = document.createElement('div');
    modal.id = 'import-duties-modal';
    modal.className = 'import-duties__modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'import-duties-modal-title');

    function row(key, label, hint, extraClass) {
      return (
        '<div class="import-duties__row' + (extraClass || '') + '" data-duties-row="' + key + '" hidden>' +
          '<span class="import-duties__row-label">' +
            escapeHtml(label) +
            (hint ? '<span class="import-duties__row-hint">' + escapeHtml(hint) + '</span>' : '') +
          '</span>' +
          '<span class="import-duties__row-value" data-duties-value="' + key + '"></span>' +
        '</div>'
      );
    }

    modal.innerHTML =
      '<div class="import-duties__modal-header">' +
        '<h3 id="import-duties-modal-title" class="import-duties__modal-title">' + escapeHtml(t('modal_title', 'Import duties & taxes')) + '</h3>' +
        '<button type="button" class="import-duties__modal-close" aria-label="' + escapeHtml(t('close', 'Close')) + '">&times;</button>' +
      '</div>' +
      '<div class="import-duties__modal-body">' +
        '<p class="import-duties__modal-intro">' + escapeHtml(
          state.isB2B
            ? t('modal_intro_b2b', 'Enter your delivery country and postal code to refine this estimate. Your Trade quote includes shipping only. Duties & taxes stay outside that quote and are billed by the carrier at import — paid directly to them, not to The Oblist.')
            : t('modal_intro', 'Enter your delivery country and postal code to refine this estimate. These amounts are not included in the product price or in shipping quotes — they are billed by the carrier at import and paid directly to them, not to The Oblist.')
        ) + '</p>' +
        '<div class="import-duties__form">' +
          '<label class="import-duties__field import-duties__field--country">' +
            '<span class="import-duties__field-label">' + escapeHtml(t('country_label', 'Country')) + '</span>' +
            '<select class="import-duties__select" data-duties-country></select>' +
          '</label>' +
          '<label class="import-duties__field import-duties__field--postal">' +
            '<span class="import-duties__field-label">' + escapeHtml(t('postal_label', 'Postal code')) + '</span>' +
            '<input type="text" class="import-duties__input" data-duties-postal autocomplete="postal-code" placeholder="10001">' +
          '</label>' +
          '<button type="button" class="import-duties__recalculate" data-duties-recalculate>' + escapeHtml(t('recalculate', 'Update estimate')) + '</button>' +
        '</div>' +
        '<p class="import-duties__status" data-duties-status aria-live="polite" hidden></p>' +
        '<div class="import-duties__breakdown" data-duties-breakdown hidden>' +
          row('duties', t('row_duties', 'Customs duties'), t('row_duties_hint', 'Assessed by customs and billed by the carrier at delivery — paid directly to the carrier, not to The Oblist.')) +
          row('taxes', t('row_taxes', 'Import taxes'), '') +
          row('fees', t('row_fees', 'Handling & processing fees'), t('row_fees_hint', 'Customs clearance and brokerage fees charged by the carrier — not The Oblist fees.')) +
          row('ddp_service_fees', t('row_ddp', 'DDP service fees'), '') +
          row('total', t('row_total', 'Estimated total'), '', ' import-duties__row--total') +
        '</div>' +
        '<p class="import-duties__disclaimer">' +
          escapeHtml(t('note', 'Not included in the product price — indicative only.')) + ' ' +
          '<a href="' + HELP_URL + '" target="_blank" rel="noopener">' + escapeHtml(t('help_link', 'Do I have to pay customs fees?')) + '</a>' +
        '</p>' +
      '</div>';

    document.body.appendChild(backdrop);
    document.body.appendChild(modal);

    modal.querySelector('.import-duties__modal-close').addEventListener('click', closeModal);
    backdrop.addEventListener('click', closeModal);

    var countrySelect = modal.querySelector('[data-duties-country]');
    var postalInput = modal.querySelector('[data-duties-postal]');

    countrySelect.addEventListener('change', function () {
      // The old postal belongs to the previous country — clear it.
      postalInput.value = '';
    });

    postalInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        recalculate();
      }
    });

    modal.querySelector('[data-duties-recalculate]').addEventListener('click', recalculate);
  }

  function syncForm() {
    var countrySelect = modal.querySelector('[data-duties-country]');
    var list = COUNTRIES.slice();
    if (state.simDest.country && !list.some(function (c) { return c.iso === state.simDest.country; })) {
      list.unshift({ iso: state.simDest.country, name: state.simDest.country });
    }
    countrySelect.innerHTML = list.map(function (c) {
      return '<option value="' + escapeHtml(c.iso) + '"' + (c.iso === state.simDest.country ? ' selected' : '') + '>' + escapeHtml(c.name) + '</option>';
    }).join('');
    modal.querySelector('[data-duties-postal]').value = state.simDest.postal || '';
  }

  // type: 'loading' (plain) | 'success' (green box) | 'error' (red box)
  //       | 'info' (neutral box, e.g. missing HS code fallback)
  function setStatus(message, type) {
    var statusEl = modal.querySelector('[data-duties-status]');
    statusEl.className = 'import-duties__status' + (type && type !== 'loading' ? ' import-duties__status--' + type : '');
    if (message) {
      statusEl.textContent = message;
      statusEl.hidden = false;
    } else {
      statusEl.textContent = '';
      statusEl.hidden = true;
    }
  }

  function setLoading(loading) {
    var btn = modal.querySelector('[data-duties-recalculate]');
    btn.disabled = !!loading;
    if (loading) setStatus(t('loading', 'Calculating estimate…'), 'loading');
  }

  function setRowValue(key, value, currency) {
    var rowEl = modal.querySelector('[data-duties-row="' + key + '"]');
    if (!rowEl) return;
    var num = typeof value === 'number' ? value : parseFloat(value);
    if (!(num > 0)) {
      rowEl.hidden = true;
      return;
    }
    rowEl.querySelector('[data-duties-value="' + key + '"]').textContent = formatAmount(num, currency);
    rowEl.hidden = false;
  }

  function renderModal(data) {
    if (!modal) return;
    var breakdownEl = modal.querySelector('[data-duties-breakdown]');

    if (!data) {
      breakdownEl.hidden = true;
      setStatus(t('loading', 'Calculating estimate…'), 'loading');
      return;
    }
    if (data.applicable === false) {
      breakdownEl.hidden = true;
      setStatus(t('same_zone', 'No import duties are expected for this destination.'), 'success');
      return;
    }
    if (isZeroDutyEstimate(data)) {
      breakdownEl.hidden = true;
      var dest = (state.simDest && state.simDest.country) || marketCountry;
      setStatus(belowThresholdModalText(dest, data), 'success');
      return;
    }
    if (data.estimable && data.estimate) {
      var currency = data.currency || state.currency;
      setRowValue('duties', data.estimate.duties, currency);
      setRowValue('taxes', data.estimate.taxes, currency);
      setRowValue('fees', data.estimate.fees, currency);
      setRowValue('ddp_service_fees', data.estimate.ddp_service_fees, currency);
      var totalEl = modal.querySelector('[data-duties-value="total"]');
      totalEl.textContent = (data.formatted && data.formatted.total) || formatAmount(data.estimate.total, currency);
      modal.querySelector('[data-duties-row="total"]').hidden = false;
      breakdownEl.hidden = false;
      setStatus('');
      return;
    }
    // Fallback (missing HS code, Arta down…) — show the API's generic copy.
    breakdownEl.hidden = true;
    var message = data.message && (data.message[state.locale] || data.message.en);
    if (message) {
      setStatus(decodeEntities(String(message)), 'info');
    } else {
      setStatus(t('error', "We couldn't calculate an estimate right now. Please try again."), 'error');
    }
  }

  function recalculate() {
    var country = (modal.querySelector('[data-duties-country]').value || '').toUpperCase();
    if (!country) return;
    var postal = modal.querySelector('[data-duties-postal]').value.trim() || defaultPostal(country);
    if (postal) modal.querySelector('[data-duties-postal]').value = postal;

    state.simDest = { country: country, postal: postal };
    saveDest(state.simDest);

    // Same Markets country → keep the postal for every PDP this session and
    // refresh the accordion line (amount + underlined postal) to match.
    if (country === marketCountry) {
      state.marketDest.postal = postal;
      state.lastKey = '';
      scheduleAccordionFetch(0);
    }

    runModalFetch();
  }

  // ── Modal open / close (a11y + iOS-safe scroll lock) ─────────────────────

  function onKeydown(e) {
    if (e.key === 'Escape') {
      closeModal();
      return;
    }
    if (e.key !== 'Tab' || !modal) return;
    var focusables = modal.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    var visible = [];
    for (var i = 0; i < focusables.length; i++) {
      if (focusables[i].offsetParent !== null) visible.push(focusables[i]);
    }
    if (!visible.length) return;
    var first = visible[0];
    var last = visible[visible.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function openModal(trigger) {
    ensureModal();
    lastTrigger = trigger || null;

    // Opening from the underlined amount / postal: show the Markets estimate
    // already on screen — no re-fetch. Form mirrors Markets country + the
    // postal we persisted (session), so the buyer can tweak it once.
    state.simDest = {
      country: state.marketDest.country,
      postal: state.marketDest.postal || ''
    };
    syncForm();

    if (state.lastData) {
      renderModal(state.lastData);
    } else if (state.modalData) {
      renderModal(state.modalData);
    } else {
      renderModal(null);
      runModalFetch();
    }

    backdrop.classList.add('is-visible');
    modal.classList.add('is-visible');

    // iOS Safari ignores body overflow:hidden — pin the body instead.
    lockedScrollY = window.scrollY || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = -lockedScrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    document.addEventListener('keydown', onKeydown);

    var closeBtn = modal.querySelector('.import-duties__modal-close');
    setTimeout(function () { closeBtn.focus(); }, 0);
  }

  function closeModal() {
    if (!modal) return;
    backdrop.classList.remove('is-visible');
    modal.classList.remove('is-visible');

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, lockedScrollY);

    document.removeEventListener('keydown', onKeydown);

    if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
    lastTrigger = null;
  }

  // ── Wiring ───────────────────────────────────────────────────────────────

  // The CTA is re-injected on every render — delegate from the root.
  root.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-import-duties-open]');
    if (!btn) return;
    e.preventDefault();
    openModal(btn);
  });

  // Variant switch — the theme dispatches variant:change with the new price
  // in cents. The Trade overlay re-runs right after; the observer below picks
  // up the rewrite, so a short debounce here is enough.
  document.addEventListener('variant:change', function (event) {
    var variant = event && event.detail && event.detail.variant;
    if (!variant || typeof variant.price !== 'number') return;
    state.basePrice = variant.price / 100;
    scheduleAccordionFetch(600);
  });

  // Trade overlay lands asynchronously (Direct Buy even awaits a pricing
  // API): watch the main sale-price element and re-fetch when the effective
  // price materially moves versus the value we last estimated with.
  var priceEl = mainPriceEl();
  if (priceEl && typeof MutationObserver === 'function') {
    var observer = new MutationObserver(function () {
      var price = effectivePrice();
      if (!(price > 0)) return;
      if (state.fetchedPrice > 0) {
        var diffPct = Math.abs(price - state.fetchedPrice) / state.fetchedPrice * 100;
        if (diffPct < MATERIAL_CHANGE_PCT) return;
      }
      scheduleAccordionFetch(400);
    });
    observer.observe(priceEl, { childList: true, subtree: true, characterData: true });
  }

  // Initial fetch — fire-and-forget once the browser is idle (never competes
  // with LCP or hydration; the SSR fallback copy is already visible and the
  // accordion is closed by default, so the estimate just needs to be ready
  // by the time the buyer opens it). For B2B customers, wait (bounded) for
  // the Trade overlay so the very first estimate is computed on the trade
  // price instead of retail + a second corrective fetch.
  function kickoff() {
    if (state.isB2B) {
      waitForTradePrice(TRADE_WAIT_MS).then(function () { runAccordionFetch(); });
    } else {
      runAccordionFetch();
    }
  }

  function scheduleKickoff() {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(kickoff, { timeout: 3000 });
    } else {
      setTimeout(kickoff, 600);
    }
  }

  requestAnimationFrame(function () {
    setTimeout(scheduleKickoff, 0);
  });
})();
